import React, {useState, useEffect, useContext, useRef} from "react";
import { EMPTY_TIMEBOOKING, ITimeBooking } from "../interfaces/ITimeBooking";
import { postZeiteintrag } from "../components/Timesafe/timesafeApiCalls";
import { Utilities } from "../Core/Utilities";
import ZeitbuchungApi from "../api/apiTimesafeZeitbuchung";
import { ApiError } from "../api/thanos26Client";
import { showFailedTimeBooking } from "../Utils/failedTimeBooking";

/** Local calendar day (YYYY-MM-DD) of an ISO timestamp, as TimeSafe expects it for "datum" */
export function toLocalDate(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// 400 = invalid booking, 422 = TimeSafe rejected it (e.g. overlapping): retrying will not help.
// Everything else (network, 401/403, 5xx) may work on the next attempt.
function isRejectedByTimeSafe(error: unknown): boolean {
    return error instanceof ApiError && (error.status === 400 || error.status === 422);
}



    // mitarbeiterID: number;  // bei start
    // tätigkeitID: number;    
    // istStempeleintrag: boolean;
    // datum: string;
    // zeitVonInMinuten: number;
    // zeitBisInMinuten: number;
    // dauerInMinuten: number;
    // dauerVerrechenbarInMinuten: number;
    // textExtern: string;
    // textIntern: string;



export const TimeBookingContext = React.createContext({
    isActive: false,
    contextTimeBookingData: EMPTY_TIMEBOOKING,
    startTimeBooking: () => { },
    beginTimeBooking: (taetigkeitId: number, text: string, taetigkeitBezeichnung: string) => { },
    loadActiveTimeBooking: async () => { },
    setIsActive: (value :boolean) => { },
    setContextTimeBookingData: (data : ITimeBooking) => { }
});

// Earlier versions kept the running timebooking only in the browser
const LEGACY_STORAGE_KEY = "active-timebooking";

function takeLegacyTimeBooking(): ITimeBooking | null {
    try {
        const stored = localStorage.getItem(LEGACY_STORAGE_KEY);
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        const booking: ITimeBooking | null = stored ? JSON.parse(stored) : null;
        return booking && booking.zeitVonInMinuten > 0 && booking.taetigkeitID > 0 ? booking : null;
    } catch {
        return null;
    }
}

export const TimeBookingProvider = ({ children }: { children: any }) => {
    // The running timebooking is stored per user in thanos26, loaded after login (loadActiveTimeBooking)
    const [isActive, setIsActive] = useState<boolean>(false);
    const [contextTimeBookingData, setContextTimeBookingData] = useState<ITimeBooking>(EMPTY_TIMEBOOKING);

    // Load the running timebooking of the signed in user (called by App after login)
    async function loadActiveTimeBooking() {
        try {
            let booking = await ZeitbuchungApi.getAktiveZeitbuchung();

            // Take over a booking still in the browser from an earlier version once
            const legacy = takeLegacyTimeBooking();
            if (!booking && legacy) {
                await ZeitbuchungApi.saveAktiveZeitbuchung(legacy);
                booking = legacy;
            }

            setContextTimeBookingData(booking ?? EMPTY_TIMEBOOKING);
            setIsActive(!!booking);
        } catch (error: any) {
            console.error(error);
            Utilities.printError(`Laufende Zeitbuchung konnte nicht geladen werden: ${error?.message ?? error}`);
        }
    }

    const setIsActiveTest = (value : boolean) : void => {
            setIsActive(value);
    };

    
/*

    useEffect(() => { 
        setIsActiveTest(false);
        setContextTimeBookingData(EMPTY_TIMEBOOKING);
    },[]);*/

    useEffect(() => { 
            console.log(`Context updated: {${JSON.stringify(contextTimeBookingData)}}`);
    },[contextTimeBookingData]);
    

    function getTimeTodayInMin(date : Date) : number {
        return date.getMinutes() + date.getHours() * 60;
    };

    async function getTimeBookingStopData(openTimebooking : ITimeBooking, text : string) : Promise<ITimeBooking>{
        let date = new Date(Date.now());
        let timeNowInMin = getTimeTodayInMin(date);
        let ts_datum = date.toISOString();
        
        let dateStart = new Date(openTimebooking.datum);
        let timeStartInMin = getTimeTodayInMin(dateStart);
        console.log(`tb stop: ${timeNowInMin}`);
        openTimebooking.zeitBisInMinuten = timeNowInMin;
        openTimebooking.dauerInMinuten = timeNowInMin - timeStartInMin; 
        openTimebooking.textExtern = text;  
        return openTimebooking;
    };

    function getTimeBookingStartData(mitarbeiterId : number, taetigkeitId: number, text : string, textIntern : string, taetigkeitBezeichnung? : string) : ITimeBooking {
        let date = new Date(Date.now());
        let timeNowInMin = getTimeTodayInMin(date);
        let ts_datum = date.toISOString();
        console.log(`tb start: ${timeNowInMin}`);
        return {
            mitarbeiterID: mitarbeiterId,
            taetigkeitID: taetigkeitId,
            istStempeleintrag: false,
            datum: ts_datum,
            zeitVonInMinuten: timeNowInMin,
            zeitBisInMinuten: 0,
            dauerInMinuten: 0,
            dauerVerrechenbarInMinuten: 0,
            textExtern: text,
            textIntern: textIntern,
            taetigkeitBezeichnung: taetigkeitBezeichnung
        };
    };

    function triggerUpdateContext(data : ITimeBooking) {
        setContextTimeBookingData(data);
    }

    // Set while clocking in/out runs: a second call (double click, Enter + click) would post the same
    // booking again, TimeSafe rejects it as overlapping with the first one and the failed-booking tab opens
    const busy = useRef<boolean>(false);

    async function startTimeBooking() {
        if (busy.current) return;
        busy.current = true;
        try {
            await toggleTimeBooking();
        } finally {
            busy.current = false;
        }
    }

    async function toggleTimeBooking() {
        if (isActive === true) {
            console.log("Timebooking already started");
            let tbd = await getTimeBookingStopData(contextTimeBookingData, contextTimeBookingData.textExtern);
            triggerUpdateContext(tbd);
            console.log(`timebooking stopped, object: {${JSON.stringify(contextTimeBookingData)}}`);
            
            let postObject : any;
            postObject = { ...tbd }; // copy: the open booking must stay intact if posting fails
            delete postObject.dauerInMinuten;  //timesafe only allows (starttime + duration) OR (starttime + endtime)
            // Billable duration is a separate value (not part of the rule above). Without it TimeSafe books 0 as
            // billable. thanos26 sends 0 for flat-rate activities (e.g. internal projects), TimeSafe rounds the rest.
            postObject.dauerVerrechenbarInMinuten = Math.max(0, tbd.zeitBisInMinuten - tbd.zeitVonInMinuten);
            delete postObject.taetigkeitBezeichnung;
            // TimeSafe takes the day from "datum", the times are local minutes: send the local day of the start.
            // The UTC timestamp (toISOString) put bookings between midnight and 1/2 am on the previous day.
            postObject.datum = toLocalDate(tbd.datum);
            console.log(`postObject: {${JSON.stringify(postObject)}}`);
            try {
                await postZeiteintrag(postObject);
                Utilities.printSuccess("Zeit in TimeSafe gebucht");
            } catch (error: any) {
                console.error(error);
                const reason = error?.message ?? String(error);
                if (!isRejectedByTimeSafe(error)) {
                    // network or server problem: keep the booking open, so it is not lost and can be stopped again
                    Utilities.printError(`Buchung fehlgeschlagen: ${reason}`);
                    return;
                }
                // TimeSafe will never accept this booking (e.g. overlapping entry): show its data in a new tab
                // so it can be entered by hand, then discard it, otherwise no new booking could be started
                showFailedTimeBooking({ ...tbd }, reason);
                Utilities.printError(`TimeSafe hat die Buchung abgelehnt, sie wurde verworfen (Details im neuen Tab): ${reason}`);
            }

            triggerUpdateContext(EMPTY_TIMEBOOKING);
            setIsActive(false);
            try {
                await ZeitbuchungApi.deleteAktiveZeitbuchung();
            } catch (error: any) {
                // booked (or discarded): do not show it as running, but warn (it would come back on reload)
                console.error(error);
                Utilities.printError(`Die laufende Buchung konnte im Backend nicht entfernt werden: ${error?.message ?? error}`);
            }
        }
         else {
            //Timebooking start -> store timestamp and data in thanos26
            let tbd = getTimeBookingStartData(
                contextTimeBookingData.mitarbeiterID,
                contextTimeBookingData.taetigkeitID,
                contextTimeBookingData.textExtern,
                contextTimeBookingData.textIntern,
                contextTimeBookingData.taetigkeitBezeichnung
            );
            console.log("timebooking started");
            if (await storeActiveTimeBooking(tbd)) {
                triggerUpdateContext(tbd);
                setIsActive(true);
            }
        }

        return;
    }

    // Clock in on a TimeSafe activity (e.g. from the home page). mitarbeiterID 0 = signed in user (set by thanos26)
    async function beginTimeBooking(taetigkeitId: number, text: string, taetigkeitBezeichnung: string) {
        if (busy.current) return;
        if (isActive) {
            Utilities.printError("Es läuft bereits eine Zeitbuchung. Bitte zuerst ausstempeln.");
            return;
        }
        busy.current = true;
        try {
            const tbd = getTimeBookingStartData(0, taetigkeitId, text, "", taetigkeitBezeichnung);
            if (await storeActiveTimeBooking(tbd)) {
                triggerUpdateContext(tbd);
                setIsActive(true);
            }
        } finally {
            busy.current = false;
        }
    }

    // Clocked in only counts once the booking is stored in thanos26 (visible on every device)
    async function storeActiveTimeBooking(timeBooking: ITimeBooking): Promise<boolean> {
        try {
            await ZeitbuchungApi.saveAktiveZeitbuchung(timeBooking);
            return true;
        } catch (error: any) {
            console.error(error);
            Utilities.printError(`Einstempeln fehlgeschlagen: ${error?.message ?? error}`);
            return false;
        }
    }

    return (
        <TimeBookingContext.Provider value={{
            isActive,
            contextTimeBookingData,
            startTimeBooking,
            beginTimeBooking,
            loadActiveTimeBooking,
            setIsActive, 
            setContextTimeBookingData,
            }}>
            {children}
        </TimeBookingContext.Provider>
    );
  };
;
